import React, { useRef, useState, useEffect } from 'react';

interface DoubleBezelCardProps {
  children: React.ReactNode;
  className?: string;
  outerClassName?: string;
  enableTilt?: boolean;
  glowColor?: 'emerald' | 'solar' | 'ozone';
}

export const DoubleBezelCard: React.FC<DoubleBezelCardProps> = ({
  children,
  className = '',
  outerClassName = '',
  enableTilt = true,
  glowColor = 'emerald',
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isFinePointer, setIsFinePointer] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    setIsFinePointer(mq.matches);

    const handler = (e: MediaQueryListEvent) => setIsFinePointer(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableTilt || !isFinePointer || !cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -3.5;
    const rotateY = ((x - centerX) / centerX) * 3.5;

    setTilt({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
  };

  const accentBorders = {
    emerald: 'hover:border-chlorophyll/40',
    solar: 'hover:border-solar-gold/50',
    ozone: 'hover:border-ozone-sky/50',
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        transform:
          isFinePointer && enableTilt && (tilt.x !== 0 || tilt.y !== 0)
            ? `perspective(1200px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(-2px)`
            : 'perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(0)',
        transition:
          isFinePointer && enableTilt && (tilt.x !== 0 || tilt.y !== 0)
            ? 'transform 0.12s ease-out'
            : 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      className={`group relative rounded-xl border border-carbon-900/10 bg-white shadow-paper hover:shadow-paper-hover transition-all duration-300 ${accentBorders[glowColor]} ${outerClassName}`}
    >
      <div className={`relative overflow-hidden p-6 sm:p-8 ${className}`}>
        {/* Archival Corner Crosshair */}
        <div className="absolute top-2 right-2 text-carbon-900/20 font-mono text-[9px] pointer-events-none select-none">
          +
        </div>
        <div className="relative z-10 h-full">{children}</div>
      </div>
    </div>
  );
};
